import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import projectService, { getProjectPhotoUrl as apiGetProjectPhotoUrl, uploadProjectPhoto as apiUploadProjectPhoto } from '../../services/projectService';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  CircularProgress, // Upewnienie się, że import jest obecny
  Chip,
  TextField,
  InputAdornment,
  InputBase
} from '@mui/material';
import OrdersTab from './OrdersTab';
import OffersTab from './OffersTab';
import PackagesTab from './PackagesTab';
import InquiriesTab from './InquiriesTab';
import ScheduleTab from './ScheduleTab';
import OrderPlanTab from './OrderPlanTab';
import ScheduleCard from './ScheduleCard';
import MobileScheduleTab from './MobileScheduleTab';
import CashFlowTab from './CashFlowTab';
import MaterialsTab from './MaterialsTab';
import ResponsiblePersonDisplay from './ResponsiblePersonDisplay';
import { IconButton, Tooltip as MuiTooltip } from '@mui/material';
import { Engineering, InfoOutlined, WarningAmberRounded, ErrorOutlineOutlined, Search } from '@mui/icons-material';
import ServicesTab from './ServicesTab'; // Importujemy nowy komponent Usługi
import GanttTimeline from './GanttTimeline'; // Importujemy nowy komponent
import SubcontractorsTab from './SubcontractorsTab';
import DraftyUmowTab from './DraftyUmowTab';
import KzmTab from './KzmTab';
import ScheduleItemForm from './ScheduleItemForm'; // Importujemy formularz
import { ProjectDisplayData } from '../../pages/ProjectsPage';
import { ScheduleItem, ScheduleItemDisplay } from '../../services/projectService'; // Importujemy interfejs ScheduleItem i ScheduleItemDisplay
import ProjectScheduleTab from './ProjectScheduleTab'; // Importujemy nowy komponent Harmonogramu
import useIsMobile from '../../hooks/useIsMobile';
import MobilePackagesList from './MobilePackagesList';
import { fetchNotifications, type AppNotification } from '../../services/notificationsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-detail-tabpanel-${index}`}
      aria-labelledby={`project-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2, minHeight: '100vh' }}>{children}</Box>}
    </div>
  );
}

interface ProjectDetailsProps {
  project: ProjectDisplayData | undefined;
  onTabChange?: (index: number) => void;
  onOpenCameras?: () => void;
  cameraPreviewUrls?: string[];
}

// Przykładowe dane pakietów
const samplePackages = [
  { 
    symbol: '1P-1', 
    name: 'Zasilanie elektryczne budowy', 
    description: 'Przygotowanie zasilania elektrycznego na potrzeby budowy',
    documentationLink: 'https://example.com/docs/general',
    packageDocumentationLink: 'https://example.com/docs/1P-1',
    estimatedValue: '15000'
  },
  { 
    symbol: '2S-1', 
    name: 'Roboty ziemne', 
    description: 'Wykopy pod fundamenty i instalacje podziemne',
    documentationLink: 'https://example.com/docs/general',
    packageDocumentationLink: 'https://example.com/docs/2S-1',
    estimatedValue: '45000'
  }
];

// Przykładowe dane zapytań
const sampleInquiries = [
  {
    id: 'INQ-123456',
    packageId: '1P-1',
    packageName: 'Zasilanie elektryczne budowy',
    contractorEmail: 'firma1@example.com',
    subject: 'FUP Warszawa 9: Zapytanie Zasilanie elektryczne budowy',
    status: 'przygotowane',
    createdAt: '2025-04-20T10:30:00Z'
  },
  {
    id: 'INQ-123457',
    packageId: '2S-1',
    packageName: 'Roboty ziemne',
    contractorEmail: 'firma2@example.com',
    subject: 'FUP Warszawa 9: Zapytanie Roboty ziemne',
    status: 'wysłane',
    createdAt: '2025-04-19T14:15:00Z'
  }
];

const ProjectDetails = forwardRef<any, ProjectDetailsProps>(({ project, onTabChange, onOpenCameras, cameraPreviewUrls }, ref) => {
  const isMobile = useIsMobile(768);
  const [tabValue, setTabValue] = useState(0);
  const [packages, setPackages] = useState<any[]>([]);
  const [inquiries, setInquiries] = useState<any[]>([]);
  const [offers, setOffers] = useState<any[]>([]);
  const [offersCount, setOffersCount] = useState<number>(0);
  const [ordersCount, setOrdersCount] = useState<number>(0); // Stan dla liczby zamówień
  const [contractDraftsCount, setContractDraftsCount] = useState<number>(0); // Stan dla liczby draftów umów
  const [scheduleItems, setScheduleItems] = useState<ScheduleItemDisplay[]>([]); // Stan dla danych harmonogramu (używamy ScheduleItemDisplay)
  const [activeTab, setActiveTab] = useState<'packages' | 'inquiries' | 'offers' | 'orders' | 'draftyUmow'>('packages');
  // Harmonogram zarządzany przez ProjectScheduleTab – lokalne stany usunięte
  const [loading, setLoading] = useState(false);
  const [refreshData, setRefreshData] = useState(0); // Nowy stan do wymuszenia odświeżenia danych
  const [selectedPackageForInquiry, setSelectedPackageForInquiry] = useState<{
    packageId: string;
    packageName: string;
    projectId: string;
    projectName: string;
  } | null>(null);
  // Zdjęcie projektu – serwer + placeholder
  const [projectPhotoSrc, setProjectPhotoSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Powiadomienia dla projektu (Rejestr zdarzeń budowy)
  const [projectNotifications, setProjectNotifications] = useState<AppNotification[]>([]);
  // Pomiar wysokości lewej kolumny (aby prawa miała stałą wysokość i scroll)
  const leftColRef = useRef<HTMLDivElement | null>(null);
  const [workersIconLoaded, setWorkersIconLoaded] = useState(false);
  // Stała wysokość kafelka rejestru na desktopie
  const RIGHT_PANEL_FIXED_HEIGHT = 665; // px
  const [workersSearch, setWorkersSearch] = useState<string>('');

  // liczba kart w dolnym bloku (Tabs)
  const TABS_COUNT = 9; // Szczegóły, Kontraktowanie, Plan zamówień, Harmonogram, KZM, Przepływy pieniężne, Materiały, Usługi, Podwykonawcy

  // Ref do głównego kontenera – aby można było ustawić fokus z zewnątrz
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Udostępnij metody sterujące przez ref (do obsługi skrótu na stronie projektów)
  useImperativeHandle(ref, () => ({
    nextTab: () => {
      setTabValue((prev) => {
        const next = (prev + 1) % TABS_COUNT;
        try { onTabChange?.(next); } catch {}
        return next;
      });
    },
    prevTab: () => {
      setTabValue((prev) => {
        const next = (prev - 1 + TABS_COUNT) % TABS_COUNT;
        try { onTabChange?.(next); } catch {}
        return next;
      });
    },
    setTab: (index: number) => {
      const normalized = ((index % TABS_COUNT) + TABS_COUNT) % TABS_COUNT;
      try { onTabChange?.(normalized); } catch {}
      setTabValue(normalized);
    },
    focus: () => {
      try { (rootRef.current as any)?.focus?.({ preventScroll: true } as any); } catch {}
    }
  }));

  // Funkcja do odświeżania danych
  const handleRefreshData = () => {
    setRefreshData(prev => prev + 1); // Inkrementacja stanu wymusza ponowne wykonanie useEffect
  };

  // Funkcja do inicjowania tworzenia zapytania z pakietu
  const handleInitiateInquiryCreation = (packageId: string, packageName: string, projectId: string, projectName: string) => {
    setSelectedPackageForInquiry({
      packageId,
      packageName,
      projectId,
      projectName
    });
    setActiveTab('inquiries'); // Przełącz na zakładkę zapytań
  };

  // Deep-link: ustaw właściwy sub‑tab na podstawie ?open=...
  React.useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const open = (sp.get('open') || '').toLowerCase();
      if (open === 'inquiries') setActiveTab('inquiries');
      else if (open === 'packages') setActiveTab('packages');
    } catch {}
  }, []);

  // Pobieranie pakietów i zapytań dla projektu
  React.useEffect(() => {
    if (project) {
      setLoading(true);
      // Spróbuj wczytać zdjęcie z serwera – bezpośredni URL (z cache-busterem)
      (async () => {
        const pid = project.project_id || project.id;
        const url = await apiGetProjectPhotoUrl(pid);
        if (url) setProjectPhotoSrc(url);
        else {
          setProjectPhotoSrc('/assets/Rectangle9.png');
        }
      })();
      
      // Sprawdź, czy mamy prawidłowy identyfikator projektu
      let projectId = project.project_id;
      
      // Sprawdź, czy project_id jest zdefiniowane
      if (!projectId) {
        console.log('project_id jest undefined lub null w ProjectDetails');
        
        // Sprawdź, czy id jest zdefiniowane i czy można je użyć
        if (project.id) {
          console.log('Używam project.id zamiast project_id w ProjectDetails:', project.id);
          projectId = project.id;
          
          // Jeśli id zawiera spację, wyodrębnij pierwszą część
          if (typeof projectId === 'string' && projectId.includes(' ')) {
            const originalId = projectId;
            projectId = projectId.split(' ')[0];
            console.log(`Wyodrębniony identyfikator projektu w ProjectDetails: "${projectId}" z "${originalId}"`);
          }
        }
      }

      if (!projectId || typeof projectId !== 'string') {
        console.error(`Nieprawidłowy lub brakujący identyfikator projektu w ProjectDetails: ${projectId}`);
        // Można rozważyć wyświetlenie błędu użytkownikowi lub ustawienie stanu błędu
        setPackages([]); // Ustaw puste tablice, aby uniknąć błędów renderowania
        setInquiries([]);
        setScheduleItems([]); // Ustaw puste dane harmonogramu
        setLoading(false);
        return;
      }
      
      console.log(`ProjectDetails: Pobieranie danych dla projektu o ID: ${projectId}`);
      
      // Pobierz pakiety dla projektu
      projectService.getPackagesByProjectId(projectId) // Używamy poprawnego projectId
        .then(packagesData => {
          setPackages(packagesData);
          return projectService.getInquiriesByProjectId(projectId);
        })
        .then(inquiriesData => {
          setInquiries(inquiriesData);
          return projectService.getOffersByProjectId(projectId);
        })
        .then(offersData => {
          setOffers(offersData);
          setOffersCount(offersData.length);
          // Pobierz zamówienia, aby uzyskać ich liczbę
          return projectService.getOrdersByProjectId(projectId); // Przekazujemy string projectId
        })
        .then(ordersData => {
          setOrdersCount(ordersData.length); // Ustaw liczbę zamówień
          return projectService.getContractDraftsByProjectId(projectId); // Pobierz drafty umów
        })
        .then(contractDraftsData => {
          setContractDraftsCount(contractDraftsData.length); // Ustaw liczbę draftów umów
        })
        // Pobierz dane harmonogramu
        .then(() => projectService.getScheduleItemsByProjectId(projectId))
        .then(scheduleData => {
          // Mapowanie danych z API do formatu ScheduleItemDisplay
          const mappedItems: ScheduleItemDisplay[] = scheduleData.map((item: ScheduleItem) => {
            // Upewniamy się, że id_task istnieje, jeśli nie, używamy id jako fallback
            const id_task_value = item.id_task || item.id.toString();
            return {
              id_task: id_task_value,
              id: item.id,
              ssp: item.ssp,
              task: item.name, // Nazwa zadania w modelu to 'name'
              days: item.days,
              startDate: item.start_date,
              endDate: item.end_date,
              predecessor: item.predecessor || '',
              value: item.value || 0,
              resource: item.resource || '',
              notes: item.notes || '',
              type: 'default', // Można dodać logikę określania typu
              expected_progress: item.expected_progress || 0,
              expected_progress_date: item.expected_progress_date || null,
              actual_progress: item.actual_progress || 0,
              actual_progress_date: item.actual_progress_date || null,
            };
          });
          setScheduleItems(mappedItems); // Ustaw dane harmonogramu
        })
        .catch(err => {
          console.error('Błąd podczas pobierania danych:', err);
          // Można ustawić stan błędu, aby wyświetlić komunikat użytkownikowi
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [project, refreshData]); // Dodajemy refreshData jako zależność

  // Wczytaj powiadomienia i przefiltruj po project_id (dla prawej kolumny – Rejestr zdarzeń budowy)
  useEffect(() => {
    const pid = String(project?.project_id || project?.id || '');
    if (!pid) return;
    let active = true;
    (async () => {
      try {
        const list = await fetchNotifications({ limit: 200, offset: 0 });
        const filtered = (list || []).filter((n: any) => {
          try {
            const ent = n && n.entity ? JSON.parse(n.entity as any) : null;
            // Najczęstsze pola z id projektu
            const candidates: Array<any> = [
              ent?.project_id,
              ent?.projectId,
              ent?.project?.id,
              ent?.project?.project_id,
              ent?.invoice?.project_id,
              ent?.data?.project_id
            ];
            if (candidates.some(v => String(v) === pid)) return true;
          } catch {}
          // Fallback: wyszukaj identyfikator projektu w tytule/wiadomości (np. faktury mają "Projekt: 228-229 ...")
          try {
            const p = String(pid).toLowerCase();
            const blob = `${String(n?.title || '')} ${String(n?.message || '')}`.toLowerCase();
            if (blob.includes(p) || blob.includes(`projekt: ${p}`)) return true;
          } catch {}
          return false;
        });
        if (active) setProjectNotifications(filtered);
      } catch {
        if (active) setProjectNotifications([]);
      }
    })();
    return () => { active = false; };
  }, [project?.project_id, project?.id, refreshData]);

  // Wysokość prawego kafelka: stała na desktopie, auto na mobile

  // Funkcje do zarządzania formularzem harmonogramu
  const handleAddScheduleItemClick = () => {
    // setEditingItem(null); // Resetuj dane edytowanego zadania
    // setIsFormVisible(true); // Pokaż formularz
  };

  const handleEditScheduleItemClick = (item: ScheduleItemDisplay) => { // Używamy ScheduleItemDisplay
    // setEditingItem(item); // Ustaw dane edytowanego zadania
    // setIsFormVisible(true); // Pokaż formularz
  };

  const handleSaveScheduleItem = () => {
    // setIsFormVisible(false); // Ukryj formularz
    // setEditingItem(null); // Wyczyść edytowane zadanie
    handleRefreshData(); // Odśwież dane harmonogramu
  };

  const handleCancelForm = () => {
    // setIsFormVisible(false); // Ukryj formularz
    // setEditingItem(null); // Wyczyść edytowane zadanie
  };

  // Upload/zmiana zdjęcia – wysyłka na serwer
  const handleChangePhotoClick = () => {
    try { fileInputRef.current?.click(); } catch {}
  };
  const handlePhotoSelected: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    (async () => {
      try {
        const pid = project?.project_id || (project as any)?.id;
        await apiUploadProjectPhoto(String(pid), file);
        // Po uploadzie ponownie pobierz świeży URL z cache-busterem
        const refreshed = await apiGetProjectPhotoUrl(String(pid));
        setProjectPhotoSrc(refreshed || '/assets/Rectangle9.png');
      } catch (err) {
        console.error('Upload zdjęcia nie powiódł się:', err);
      }
    })();
    // wyczyść input, aby to samo zdjęcie można było wybrać ponownie
    e.target.value = '';
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    try { onTabChange?.(newValue); } catch {}
  };

  // Sprawdzenie, czy projekt istnieje
  if (!project) {
    return (
      <Paper sx={{ mt: 4, p: 3 }}>
        <Typography variant="body1">
          Nie wybrano projektu lub projekt nie istnieje.
        </Typography>
      </Paper>
    );
  }

  // Wyświetlanie stanu ładowania
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={40} sx={{ color: '#004273' }} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Ładowanie danych projektu...
        </Typography>
      </Box>
    );
  }

  // Proste metryki do prawego panelu
  const computedProgress = (() => {
    if (scheduleItems && scheduleItems.length > 0) {
      const values = scheduleItems.map(it => {
        const ap = Number(it.actual_progress || 0);
        const ep = Number(it.expected_progress || 0);
        return isFinite(ap) && ap > 0 ? ap : ep;
      });
      const avg = values.reduce((a, b) => a + (isFinite(b) ? b : 0), 0) / values.length;
      return Math.max(0, Math.min(100, Math.round(avg)));
    }
    // fallback
    return 0;
  })();
  const computedDaysLeft = (() => {
    const end = (project as any)?.end_date || (project as any)?.endDate || (project as any)?.work_completion_deadline;
    const endDate = end ? new Date(end) : null;
    if (!endDate || isNaN(endDate.getTime())) return null;
    const now = new Date();
    const diffMs = endDate.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  })();
  const workersOnSite: number | null = null; // brak danych – placeholder

  return (
    <Paper ref={rootRef} tabIndex={0} sx={{ 
      mt: 4, 
      p: 0, 
      overflow: 'hidden',
      // Dopasowanie do tła aplikacji – bez widocznej „karty”
      backgroundColor: '#FAFAFA',
      boxShadow: 'none',
      border: '1px solid #FAFAFA',
      outline: 'none'
    }}>
      {/* Stary nagłówek i zakładki usunięte – obsługa zakładek przeniesiona do nagłówka strony */}

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ px: 0 }}>
          {/* Nowy moduł (Frame 431): zdjęcie + opis oraz dwie karty po prawej */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
            {/* Lewy blok (Frame 436): zdjęcie + podstawowe informacje */}
            <Box
              sx={{
                display: 'flex',
                border: '1px solid #EDEDED',
                borderRadius: '4px',
                overflow: 'hidden',
                minHeight: 208,
                flex: 1,
                bgcolor: '#fff'
              }}
            >
              {/* Zdjęcie (Rectangle 8) */}
              <Box sx={{ position: 'relative', width: 291, flexShrink: 0, backgroundColor: '#D9D9D9' }}>
                {projectPhotoSrc ? (
                  <Box
                    component="img"
                    src={projectPhotoSrc}
                    alt="Zdjęcie projektu"
                    onError={(e: any) => {
                      const step = Number(e?.currentTarget?.getAttribute('data-fallback-step') || '0');
                      const fallbacks = ['/assets/Rectangle9.png', '/assets/rectangle-9.png', '/assets/rectangle9.png'];
                      if (step < fallbacks.length) {
                        e.currentTarget.setAttribute('data-fallback-step', String(step + 1));
                        e.currentTarget.src = fallbacks[step];
                        return;
                      }
                      setProjectPhotoSrc(null);
                    }}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>
                      Zdjęcie projektu
                    </Typography>
                  </Box>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoSelected}
                />
                <IconButton
                  onClick={handleChangePhotoClick}
                  aria-label="Zmień zdjęcie"
                  sx={{
                    position: 'absolute',
                    right: 12,
                    bottom: 12,
                    p: 0.75,
                    m: 0,
                    width: 'auto',
                    height: 'auto',
                    bgcolor: 'rgba(255,255,255,0.7)',
                    borderRadius: '999px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    backdropFilter: 'blur(2px)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/edit_image.png"
                    alt="Edytuj"
                    onError={(e: any) => {
                      const tried = e?.currentTarget?.getAttribute('data-tried') || '';
                      if (!tried) {
                        e.currentTarget.setAttribute('data-tried', '1');
                        e.currentTarget.src = '/Users/konradtrzeszczkowski/Downloads/edit_image.png';
                      }
                    }}
                    sx={{ display: 'block', width: 'auto', height: 'auto' }}
                  />
                </IconButton>
              </Box>

              {/* Prawy panel treści (Frame 423) */}
              <Box sx={{ flex: 1, backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: '24px', pt: '24px', pb: 0 }}>
                  <Box sx={{ display: 'grid', rowGap: 0 }}>
                    {[
                      { label: 'Przedmiot umowy', value: project.contract_subject || '-' },
                      { label: 'Numer umowy', value: project.contract_number || '-' },
                      { label: 'Data podpisania', value: project.contract_sign_date || '-' },
                      { label: 'Adres inwestycji', value: project.investment_address || '-' },
                      { label: 'Klient', value: project.client_name || '-' }
                    ].map((row, idx) => (
                      <Box
                        key={row.label}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          height: 32,
                          backgroundColor: idx % 2 === 1 ? '#F9F9F9' : '#FFFFFF',
                          px: 0
                        }}
                      >
                        <Box sx={{ width: 130, pr: 2 }}>
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>
                            {row.label}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontFamily: 'Roboto, sans-serif',
                              fontSize: '14px',
                              color: '#020242',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={String(row.value)}
                          >
                            {row.value}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Prawa karta 1: Zaawansowanie prac */}
            <Box
              sx={{
                width: 214.5,
                minWidth: 214,
                bgcolor: '#fff',
                border: '1px solid #EDEDED',
                borderRadius: '4px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography sx={{ color: '#595966', fontSize: 14, px: '24px', pt: '24px' }}>Zaawansowanie prac</Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, px: '24px', pb: '24px' }}>
                <Box sx={{ width: 80, height: 80, display: 'grid', placeItems: 'center' }}>
                  {(() => {
                    const size = 80;
                    const stroke = 15;
                    const center = size / 2;
                    const radius = center - stroke / 2;
                    const circumference = 2 * Math.PI * radius;
                    const pct = Math.max(0, Math.min(100, computedProgress));
                    const dashoffset = circumference * (1 - pct / 100);
                    return (
                      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                        {/* Szara ścieżka tła (pusta w środku) */}
                        <circle
                          cx={center}
                          cy={center}
                          r={radius}
                          stroke="#E6E6EC"
                          strokeWidth={stroke}
                          fill="none"
                        />
                        {/* Niebieski łuk postępu */}
                        <circle
                          cx={center}
                          cy={center}
                          r={radius}
                          stroke="#4040DB"
                          strokeWidth={stroke}
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={`${circumference} ${circumference}`}
                          strokeDashoffset={dashoffset}
                          transform={`rotate(-90 ${center} ${center})`}
                        />
                      </svg>
                    );
                  })()}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography sx={{ fontSize: 32, lineHeight: '38px', color: '#020242' }}>
                    {Math.max(0, Math.min(100, computedProgress))}%
                  </Typography>
                  <Typography sx={{ fontSize: 14, lineHeight: '16px', color: '#020242' }}>
                    {computedDaysLeft != null ? `${computedDaysLeft} dni` : '—'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Prawa karta 2: Pracownicy na budowie */}
            <Box
              sx={{
                width: 214.5,
                minWidth: 214,
                bgcolor: '#fff',
                border: '1px solid #EDEDED',
                borderRadius: '4px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography sx={{ color: '#595966', fontSize: 14, px: '24px', pt: '24px' }}>Pracownicy na budowie</Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, px: '24px' }}>
                {/* Ikona z pliku w public (obsługa spacji w nazwie); fallback do ikony MUI gdy plik nie jest dostępny */}
                <Box sx={{ position: 'relative', width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {!workersIconLoaded && <Engineering sx={{ position: 'absolute', width: 60, height: 60, color: '#595966' }} />}
                  <Box
                    component="img"
                    src={'/assets/icons/person.svg'}
                    alt="Pracownicy"
                    onLoad={() => setWorkersIconLoaded(true)}
                    onError={() => setWorkersIconLoaded(false)}
                    sx={{ width: 60, height: 60, display: workersIconLoaded ? 'block' : 'none' }}
                  />
                </Box>
                <Typography sx={{ fontSize: 32, lineHeight: '38px', color: '#020242' }}>
                  {workersOnSite != null ? workersOnSite : '—'}
                </Typography>
              </Box>
              <Box sx={{ px: '24px', pb: '24px' }}>
                <Typography component="a" sx={{ fontSize: 14, color: '#1F1FB0', textDecoration: 'none', cursor: 'pointer' }}>
                  Zobacz listę
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Nowy wiersz poniżej – lewa i prawa kolumna, odstęp 16px */}
          <Box sx={{ mt: '16px', display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: 'wrap' }}>
            {/* LEWA KOLUMNA (711px) */}
            <Box ref={leftColRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: { xs: '100%', md: 651 }, maxWidth: '100%', flexGrow: 1 }}>
              {/* Sekcja 1: Terminy i wartości */}
              <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '8px', p: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: '#2D2D32',
                  px: '24px',
                  pt: '24px',
                  pb: '8px'
                }}
              >
                Terminy i wartości
              </Typography>
              {[
                { label: 'Data rozpoczęcia', value: project.start_date || '-' },
                { label: 'Data zakończenia', value: project.end_date || '-' },
                { label: 'Wartość', value: project.value ? `${Number(project.value).toLocaleString('pl-PL')}` : '-' },
                { label: 'Budżet', value: project.budget ? `${Number(project.budget).toLocaleString('pl-PL')}` : '-' },
                { label: 'Okres gwarancji', value: project.warranty_period_months ? `${project.warranty_period_months} miesięcy` : '-' }
              ].map((row, idx) => (
                <Box
                  key={row.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 48,
                    backgroundColor: idx % 2 === 1 ? '#F9F9F9' : '#FFFFFF',
                    px: '24px'
                  }}
                >
                  <Box sx={{ width: 180, pr: 2 }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>
                      {row.label}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#020242' }} title={String(row.value)}>
                      {row.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
              </Box>

              {/* Sekcja 2: Dokumenty i media */}
              <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '4px', p: 0 }}>
                <Typography
                  sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '16px',
                    lineHeight: '19px',
                    fontWeight: 500,
                    color: '#2D2D32',
                    px: '24px',
                    pt: '24px',
                    pb: '8px'
                  }}
                >
                  Dokumenty i media
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: '24px', pb: '16px' }}>
                  {[
                    { label: 'Google Drive', href: project.google_drive_link },
                    { label: 'Collectifile', href: project.miro_link },
                    { label: 'Szablon umowy 1', href: project.contract_draft_1 },
                    { label: 'Szablon umowy 2', href: project.contract_draft_2 },
                    { label: 'Szablon KZM', href: project.kzm_draft },
                    { label: 'Szablon POKR', href: project.pokr_draft },
                    { label: 'Szablon POCR', href: project.pocr_draft }
                  ].map((item) => (
                    <Box
                      key={item.label}
                      component={item.href ? 'a' : 'div'}
                      href={item.href || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1.5,
                        height: 32,
                        borderRadius: '4px',
                        backgroundColor: '#F5F5FF',
                        color: '#1F1FB0',
                        textDecoration: 'none',
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        cursor: item.href ? 'pointer' : 'default',
                        opacity: item.href ? 1 : 0.5
                      }}
                    >
                      {item.label}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Sekcja 3: Kamery na budowie */}
              <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '4px', p: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '24px', pt: '24px', pb: '8px' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '16px',
                      lineHeight: '19px',
                      fontWeight: 500,
                      color: '#2D2D32'
                    }}
                  >
                    Kamery na budowie
                  </Typography>
                  {onOpenCameras ? (
                    <Typography
                      component="a"
                      onClick={() => onOpenCameras()}
                      sx={{ fontSize: 14, color: '#1F1FB0', textDecoration: 'none', cursor: 'pointer' }}
                    >
                      Zobacz wszystkie ({(cameraPreviewUrls || []).length || 4})
                    </Typography>
                  ) : null}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, px: '24px', pb: '24px' }}>
                  {(cameraPreviewUrls && cameraPreviewUrls.length ? cameraPreviewUrls.slice(0, 3) : [null, null, null]).map((u, idx) => {
                    // Budujemy zestaw kandydatów na miniaturę – najpierw override z localStorage/env, potem heurystyki z URL HLS
                    const buildCandidates = (src?: string | null): string[] => {
                      const list: string[] = [];
                      try {
                        const override = localStorage.getItem(`camera:magazyn:cam${idx + 1}:thumb`);
                        if (override && override.trim()) list.push(override.trim());
                      } catch {}
                      const envKey = `REACT_APP_CAMERA_MAGAZYN_CAM${idx + 1}_THUMB_URL` as keyof NodeJS.ProcessEnv;
                      const envUrl = (process.env[envKey] as string | undefined)?.trim?.() || '';
                      if (envUrl) list.push(envUrl);
                      const base = String(src || '').trim();
                      // URL z proxy API (HTTPS) — heurystyka snapshot.jpg zwykle nie ma pliku na Pi → tylko 404 w sieci
                      if (base.includes('/hardware/') && base.includes('/hls/')) {
                        list.push('/assets/Rectangle9.png');
                        return list;
                      }
                      if (base) {
                        const repl = (from: string, to: string) => {
                          if (base.includes(from)) list.push(base.replace(from, to));
                        };
                        repl('index.m3u8', 'snapshot.jpg');
                        repl('index.m3u8', 'snapshot.jpeg');
                        repl('index.m3u8', 'snapshot.png');
                        repl('index.m3u8', 'thumb.jpg');
                        repl('index.m3u8', 'thumb.png');
                        repl('index.m3u8', 'poster.jpg');
                        repl('live.m3u8', 'snapshot.jpg');
                      }
                      // Ogólny placeholder jeśli nic nie zadziała
                      list.push('/assets/Rectangle9.png');
                      return list;
                    };
                    const candidates = buildCandidates(u);
                    return (
                      <Box
                        key={idx}
                        onClick={() => onOpenCameras?.()}
                        sx={{
                          height: 120,
                          bgcolor: '#D9D9D9',
                          borderRadius: '4px',
                          cursor: onOpenCameras ? 'pointer' : 'default',
                          overflow: 'hidden',
                          position: 'relative'
                        }}
                        title="Kliknij, aby otworzyć podgląd kamer"
                      >
                        {u ? (
                          <Box
                            component="img"
                            src={candidates[0]}
                            data-step={0}
                            data-candidates={encodeURIComponent(JSON.stringify(candidates))}
                            alt=""
                            onError={(e: any) => {
                              try {
                                const el = e.currentTarget as HTMLImageElement;
                                const acc = JSON.parse(decodeURIComponent(el.getAttribute('data-candidates') || '[]')) as string[];
                                let step = Number(el.getAttribute('data-step') || '0');
                                step++;
                                if (step < acc.length) {
                                  el.setAttribute('data-step', String(step));
                                  el.src = acc[step];
                                  return;
                                }
                              } catch {}
                            }}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* PRAWA KOLUMNA (501px) */}
            <Box sx={{ width: { xs: '100%', md: 561 }, maxWidth: '100%', display: 'flex', alignSelf: 'stretch', height: !isMobile ? `${RIGHT_PANEL_FIXED_HEIGHT}px` : 'auto' }}>
              <Box sx={{ bgcolor: '#fff', border: '1px solid #EDEDED', borderRadius: '8px', p: 0, boxSizing: 'border-box', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', flex: 1, minHeight: !isMobile ? `${RIGHT_PANEL_FIXED_HEIGHT}px` : 0, overflow: 'hidden', height: !isMobile ? `${RIGHT_PANEL_FIXED_HEIGHT}px` : 'auto', maxHeight: !isMobile ? `${RIGHT_PANEL_FIXED_HEIGHT}px` : undefined }}>
                <Box sx={{ px: '24px', pt: '24px', pb: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '16px',
                      lineHeight: '19px',
                      fontWeight: 500,
                      color: '#2D2D32'
                    }}
                  >
                    Rejestr zdarzeń budowy
                  </Typography>
                </Box>
                {/* Pasek – Searchbar jak w okienku Rejestru zdarzeń (szare wypełnienie) */}
                <Box sx={{ mx: '24px', mt: '16px', mb: 0 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 0,
                      py: 0,
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: '4px',
                      backgroundColor: '#f9f9f9',
                      border: '1px solid #ededed',
                      height: 36,
                      width: '100%',
                      '&:hover': { borderColor: '#B9B9B9' }
                    }}
                  >
                    <Box component="img" src="/assets/topbar/search.svg" alt="search" sx={{ ml: 1.5 }} />
                    <InputBase
                      placeholder="Szukaj zdarzenia"
                      value={workersSearch}
                      onChange={(e)=>setWorkersSearch(e.target.value)}
                      sx={{ pl: 1.5, pr: 1.5, flex: 1, fontSize: 14 }}
                      inputProps={{ 'aria-label': 'szukaj zdarzenia' }}
                    />
                  </Paper>
                </Box>

                {/* Lista aktualizacji – przewijana tak, by kafelek kończył się równo z „Kamery na budowie” */}
                 <Box sx={{ px: '24px', pt: 2, pb: '16px', display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  {projectNotifications.slice(0, 12).map((n, idx) => {
                    let category: 'info' | 'warning' | 'danger' = 'info';
                    const t = String(n.type || '').toLowerCase();
                    if (t.includes('warn')) category = 'warning';
                    if (t.includes('error') || t.includes('danger') || t.includes('fail')) category = 'danger';
                    let subtitle: string | undefined = undefined;
                    // Linki jak w szufladzie powiadomień
                    let route: string | undefined;
                    let externalLink: string | undefined;
                    try {
                      const ent = (n as any).entity ? JSON.parse((n as any).entity) : null;
                      if (ent && ent.message) subtitle = String(ent.message);
                      if (ent) {
                        if (ent.route && typeof ent.route === 'string') {
                          if (String(n.type || '').includes('package')) {
                            externalLink = String(ent.route);
                          } else {
                            route = String(ent.route);
                          }
                        }
                        if (ent.pdf_link) {
                          const value = String(ent.pdf_link || '').trim();
                          if (value) {
                            if (value.includes('docs.google.com/document/')) {
                              const m = value.match(/\/document\/d\/([a-zA-Z0-9_-]+)/);
                              if (m && m[1]) {
                                const exportUrl = `https://docs.google.com/document/d/${m[1]}/export?format=pdf`;
                                externalLink = `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(exportUrl)}`;
                              }
                            } else {
                              const byFile = value.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                              const byPlain = value.match(/\/(?:u\/\d+\/)?d\/([a-zA-Z0-9_-]+)/);
                              const byQuery = value.match(/[?&]id=([a-zA-Z0-9_-]+)/);
                              const id = (byFile && byFile[1]) || (byPlain && byPlain[1]) || (byQuery && byQuery[1]) || null;
                              externalLink = id ? `https://drive.google.com/file/d/${id}/preview` : value;
                            }
                          }
                        } else if ((n.type === 'transfer_invoice') && ent.id) {
                          // Powiadomienie o fakturze – jak w szufladzie powiadomień
                          externalLink = `/accounting/purchases?invoice=${encodeURIComponent(ent.id)}`;
                        } else if ((String(n.type || '').includes('inquiry')) && ent.project_id && ent.id) {
                          externalLink = `/projects/${encodeURIComponent(ent.project_id)}?open=inquiries&inquiry=${encodeURIComponent(ent.id)}`;
                        } else if ((n.type === 'package.created' || n.type === 'package') && ent.project_id) {
                          externalLink = ent.route ? String(ent.route) : `/projects/${encodeURIComponent(ent.project_id)}?open=packages`;
                          route = undefined;
                        } else if (ent.link || ent.doc_link) {
                          const raw = String(ent.link || ent.doc_link);
                          if (raw.includes('docs.google.com/document/')) {
                            externalLink = raw;
                          } else {
                            const byFile = raw.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                            const byPlain = raw.match(/\/(?:u\/\d+\/)?d\/([a-zA-Z0-9_-]+)/);
                            const byQuery = raw.match(/[?&]id=([a-zA-Z0-9_-]+)/);
                            const id = (byFile && byFile[1]) || (byPlain && byPlain[1]) || (byQuery && byQuery[1]) || null;
                            externalLink = id ? `https://drive.google.com/file/d/${id}/preview` : raw;
                          }
                        }
                      }
                    } catch {}
                    const formatTime = (iso: string) => {
                      try {
                        const d = new Date(iso);
                        if (isNaN(d.getTime())) return iso;
                        const pad = (x: number) => String(x).padStart(2, '0');
                        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                      } catch { return n.created_at; }
                    };
                    return (
                    <Box
                      key={idx}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.2,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: '#F5F5FF' },
                        cursor: (route || externalLink) ? 'pointer' : 'default'
                      }}
                      onClick={() => {
                        try {
                          if (externalLink) {
                            window.open(externalLink as string, '_blank', 'noopener');
                            return;
                          }
                          if (route) {
                            window.location.href = route as string;
                          }
                        } catch {}
                      }}
                    >
                      <Box sx={{ mt: '2px' }}>
                        {category === 'info' ? (
                          <InfoOutlined sx={{ width: 24, height: 24, color: '#2020AF' }} />
                        ) : category === 'warning' ? (
                          <WarningAmberRounded sx={{ width: 24, height: 24, color: '#B25A00' }} />
                        ) : (
                          <ErrorOutlineOutlined sx={{ width: 24, height: 24, color: '#B3001E' }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                          <Typography sx={{ fontSize: 14, color: '#2D2D32' }}>{n.title}</Typography>
                          {!!(subtitle || n.message) && (
                            <Typography sx={{ fontSize: 14, color: '#595966', mt: 0.5 }}>{subtitle || n.message}</Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, justifyContent: 'space-between', width: '100%' }}>
                          <Chip
                            label={formatTime(n.created_at)}
                            size="small"
                            sx={{
                              bgcolor: '#FFF5CC',
                              color: '#020242',
                              borderRadius: '4px',
                              minHeight: 21,
                              height: 21,
                              '& .MuiChip-label': {
                                px: 1,
                                py: 0,
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '11px',
                                fontWeight: 400,
                                lineHeight: '13px',
                                color: '#020242'
                              }
                            }}
                          />
                          {(route || externalLink) && (
                            <Chip
                              label="Przejdź"
                              size="small"
                              clickable
                              color="primary"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (externalLink) {
                                  try { window.open(externalLink as string, '_blank', 'noopener'); } catch {}
                                  return;
                                }
                                if (route) {
                                  try { window.location.href = route as string; } catch {}
                                }
                              }}
                              sx={{
                                height: 18,
                                borderRadius: '4px',
                                fontWeight: 700,
                                fontSize: 10,
                                ml: 'auto',
                                '& .MuiChip-label': { px: 0.8 },
                                '&:hover': { backgroundColor: 'primary.light', color: '#fff', borderColor: 'primary.light' }
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                    );
                  })}
                  {projectNotifications.length === 0 && (
                    <Typography sx={{ px: 0, py: 1, color: '#595966', fontSize: 14 }}>Brak zdarzeń dla tego projektu.</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ px: 0 }}>
          <Box sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mb: 0 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                aria-label="contracting subtabs"
                sx={{
                  minHeight: 40,
                  height: 40,
                  flexGrow: 1,
                  '& .MuiTabs-indicator': { display: 'none' },
                  '& .MuiTabs-flexContainer': { gap: '2px' }
                }}
              >
                <Tab
                  disableRipple
                  value="packages"
                  label={`Pakiety (${packages.length})`}
                  sx={{
                    textTransform: 'none',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    px: 3,
                    minHeight: 32,
                    height: 32,
                    mt: '8px',
                    bgcolor: '#F1F1F6',
                    borderRadius: '4px 4px 0 0',
                    '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                  }}
                />
                <Tab
                  disableRipple
                  value="inquiries"
                  label={`Zapytania (${inquiries.length})`}
                  sx={{
                    textTransform: 'none',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    px: 3,
                    minHeight: 32,
                    height: 32,
                    mt: '8px',
                    bgcolor: '#F1F1F6',
                    borderRadius: '4px 4px 0 0',
                    '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                  }}
                />
                <Tab
                  disableRipple
                  value="offers"
                  label={`Zestawienie ofert (${offersCount})`}
                  sx={{
                    textTransform: 'none',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    px: 3,
                    minHeight: 32,
                    height: 32,
                    mt: '8px',
                    bgcolor: '#F1F1F6',
                    borderRadius: '4px 4px 0 0',
                    '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                  }}
                />
                <Tab
                  disableRipple
                  value="orders"
                  label={`Zamówienia (${ordersCount})`}
                  sx={{
                    textTransform: 'none',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    px: 3,
                    minHeight: 32,
                    height: 32,
                    mt: '8px',
                    bgcolor: '#F1F1F6',
                    borderRadius: '4px 4px 0 0',
                    '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                  }}
                />
                <Tab
                  disableRipple
                  value="draftyUmow"
                  label={`Drafty umów z PW (${contractDraftsCount})`}
                  sx={{
                    textTransform: 'none',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    px: 3,
                    minHeight: 32,
                    height: 32,
                    mt: '8px',
                    bgcolor: '#F1F1F6',
                    borderRadius: '4px 4px 0 0',
                    '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                  }}
                />
              </Tabs>
            </Box>
            
            {/* Tabela zamówień */}
            {activeTab === 'orders' && (
              // Przekazujemy pełny string projectId
              <OrdersTab projectId={project.project_id || project.id} /> 
            )}

            {/* Tabela ofert */}
            {activeTab === 'offers' && (
              <OffersTab projectId={project.project_id || project.id} projectName={project.name} onCountChange={setOffersCount} />
            )}

            {/* Pakiety: desktop tabela, mobile uproszczona lista */}
            {activeTab === 'packages' && (
              isMobile ? (
                <MobilePackagesList projectId={project.project_id || project.id} projectName={project.name} />
              ) : (
                <PackagesTab 
                  projectName={project.name} 
                  projectData={project} 
                  onInitiateInquiryCreation={handleInitiateInquiryCreation}
                />
              )
            )}

            {/* Tabela zapytań */}
            {activeTab === 'inquiries' && (
              <InquiriesTab 
                projectName={project.name} 
                projectData={project} 
                onRefreshData={handleRefreshData}
                initialPackageForInquiry={selectedPackageForInquiry}
                onPackageInquiryProcessed={() => setSelectedPackageForInquiry(null)}
              />
            )}

            {/* Drafty umów z PW */}
            {activeTab === 'draftyUmow' && (
              <DraftyUmowTab projectName={project.name} projectId={project.project_id || project.id} />
            )}

            {/* KZM zostało przeniesione na główną kartę */}
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ px: 3 }}>
          <OrderPlanTab projectId={project.project_id || project.id} />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ px: 3 }}>
          {/* Hook musi być wywołany na top-level komponentu, przenosimy wynik do zmiennej wyżej */}
          {/* Wyświetlenie zależne od flagi przekazanej z top-level */}
          {isMobile ? (
            <MobileScheduleTab projectId={project.project_id || project.id} />
          ) : (
            <ScheduleCard projectId={project.project_id || project.id} projectName={project.name} />
          )}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Box sx={{ px: 0 }}>
          <KzmTab projectName={project.name} projectData={project} />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <Box sx={{ px: 3 }}>
          <CashFlowTab projectName={project.name} />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <Box sx={{ px: 3 }}>
          <MaterialsTab projectName={project.name} projectId={project.project_id || project.id} />
        </Box>
      </TabPanel>

      {/* Nowy TabPanel dla Usług */}
      <TabPanel value={tabValue} index={7}>
        <Box sx={{ px: 3 }}>
          <ServicesTab projectName={project.name} projectId={project.project_id || project.id} />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={8}>
        <Box sx={{ px: 3 }}>
          <SubcontractorsTab
            projectName={project.name}
            projectId={project.project_id || project.id}
            openDetailsForSubId={new URLSearchParams(window.location.search).get('subId') || undefined}
          />
        </Box>
      </TabPanel>
    </Paper>
  );
});

export default ProjectDetails;
